import { useEffect } from 'react';
import { useDisclosure } from '@mantine/hooks';
import {
  Button,
  Modal,
  TextInput,
  Textarea,
  Text,
  Group,
  rem,
} from '@mantine/core';
import { createFormContext, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { IconThumbUp, IconX } from '@tabler/icons-react';
import {
  type IssueReport,
  issueReportSchema,
  defaultIssueReport,
} from '@/schemas/API/POST/bug-report/request';
import { postFetcher } from '@/api/fetchers';

export const [
  IssueReportFormProvider,
  useIssueReportFormContext,
  useIssueReportForm,
] = createFormContext<IssueReport>();

export const IssueReportComponent = (): JSX.Element => {
  const [opened, { open, close }] = useDisclosure(false);
  const form = useIssueReportForm({
    validate: zodResolver(issueReportSchema),
  });

  useEffect(() => {
    form.setInitialValues(defaultIssueReport);
    form.setValues(defaultIssueReport);
  }, []);

  const handleCancel = (): void => {
    if (!form.isDirty()) {
      close();
      return;
    }

    modals.openConfirmModal({
      title: 'Odejít bez uložení?',
      centered: true,
      children: (
        <Text>
          Formulář obsahuje změny, které nebyly uloženy. Určitě chcete zavřít
          okno?
        </Text>
      ),
      labels: { confirm: 'Odejít', cancel: 'Zrušit' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        close();
        form.reset();
      },
    });
  };

  const handleSubmit = async (): Promise<void> => {
    if (form.validate().hasErrors) {
      notifications.show({
        title: 'Ve formuláři se vyskytla chyba',
        message: 'Zkontrolujte, že položky nejsou prázdné',
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
      return;
    }

    try {
      await postFetcher('/issues', form.values);

      notifications.show({
        title: 'Děkujeme za zpětnou vazbu',
        message: 'Chybu se pokusíme opravit co nejdříve',
        color: 'blue',
        icon: <IconThumbUp style={{ width: rem(20), height: rem(20) }} />,
      });

      close();
      form.reset();
    } catch (error) {
      notifications.show({
        title: 'Odeslání selhalo',
        message: 'Nepodařilo se odeslat hlášení. Zkuste to prosím znovu.',
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
      console.error('Bug report submission failed:', error);
    }
  };

  return (
    <IssueReportFormProvider form={form}>
      <Button
        className="text-slate-50 text-heading2 outline outline-2 outline-red-500 flex justify-start rounded-card transition-colors bg-red-500/50 hover:bg-red-500/75"
        fullWidth
        variant="subtle"
        onClick={() => open()}
      >
        Nahlásit chybu
      </Button>

      <Modal
        opened={opened}
        onClose={handleCancel}
        title="Nahlásit chybu"
        centered
        size={'xl'}
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <TextInput
            label="Název chyby"
            placeholder="Zadejte název (jednoduchý krátký popis)"
            withAsterisk
            error={form.errors.title}
            {...form.getInputProps('title')}
          />

          <Textarea
            label="Popis"
            placeholder="Popište chybu (kdy a jak se projevuje, jak moc omezující je, ...)"
            autosize
            minRows={5}
            className="my-2"
            withAsterisk
            error={form.errors.description}
            {...form.getInputProps('description')}
          />

          <Group justify="flex-end" mt={12}>
            <Button variant="outline" onClick={handleCancel}>
              Zrušit
            </Button>
            <Button className="bg-blue-500 hover:bg-blue-600" type="submit">
              Odeslat
            </Button>
          </Group>
        </form>
      </Modal>
    </IssueReportFormProvider>
  );
};
